import * as React from 'react';
import { Button, Space, message, Form } from 'antd';
import { SyncOutlined, DollarOutlined } from '@ant-design/icons';
import axios from 'axios';
import { ConfirmModal } from './ConfirmModal';
import { CommaSeparatedInput } from './CommaSeparatedInput';
import { getIdToken } from '../firebase/auth';

interface Shop {
    id: string;
    url: string;
    name?: string;
    [key: string]: any;
}

interface ShopActionsProps {
    shop: Shop;
}

const BUTTON_WIDTH = 180;
const SYNC_INVENTORY_API_URL = process.env.REACT_APP_SYNC_INVENTORY_API_URL;
const SYNC_PRICES_API_URL = process.env.REACT_APP_SYNC_PRICES_API_URL;

export function ShopActions({ shop }: ShopActionsProps): React.ReactElement {
    const [syncInventoryModalOpen, setSyncInventoryModalOpen] = React.useState<boolean>(false);
    const [syncInventoryLoading, setSyncInventoryLoading] = React.useState<boolean>(false);
    const [barcodes, setBarcodes] = React.useState<string[]>([]);
    const [syncPricesModalOpen, setSyncPricesModalOpen] = React.useState<boolean>(false);
    const [syncPricesLoading, setSyncPricesLoading] = React.useState<boolean>(false);
    const [promotionIds, setPromotionIds] = React.useState<string[]>([]);
    const [isMobile, setIsMobile] = React.useState<boolean>(window.innerWidth < 768);

    React.useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSyncInventoryClick = () => {
        setBarcodes([]);
        setSyncInventoryModalOpen(true);
    };

    const handleSyncInventoryConfirm = async () => {
        setSyncInventoryLoading(true);

        const idToken = await getIdToken();
        if (!idToken) {
            message.error('Not signed in — please sign in again');
            setSyncInventoryLoading(false);
            return;
        }

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
        };

        const shopId = String(shop.id || shop.url || '').trim();

        if (!shopId) {
            message.error('Shop ID is missing');
            setSyncInventoryLoading(false);
            return;
        }

        if (!SYNC_INVENTORY_API_URL) {
            message.error('SYNC_INVENTORY_API_URL is not set');
            setSyncInventoryLoading(false);
            return;
        }

        const url = new URL(SYNC_INVENTORY_API_URL);
        url.searchParams.set('shopId', shopId);
        const apiUrlWithQuery = url.toString();

        const payload: Record<string, any> = {
            force: true,
            trigger: 'manual'
        };

        if (barcodes.length > 0) {
            payload.barcodes = barcodes;
        }

        axios.post(apiUrlWithQuery, payload, {
            headers: headers
        }).catch((error) => {
            if (error?.response?.status === 401 || error?.response?.status === 403) {
                message.error('Not authorized to sync this shop');
            }
            console.error('Error syncing inventory (fire-and-forget):', error);
        });
        
        message.success('Inventory sync started successfully');
        setSyncInventoryModalOpen(false);
        setSyncInventoryLoading(false);
        setBarcodes([]);
    };

    const handleSyncInventoryCancel = () => {
        setSyncInventoryModalOpen(false);
    };

    const handleSyncPricesClick = () => {
        setPromotionIds([]);
        setSyncPricesModalOpen(true);
    };

    const handleSyncPricesConfirm = async () => {
        setSyncPricesLoading(true);

        const idToken = await getIdToken();
        if (!idToken) {
            message.error('Not signed in — please sign in again');
            setSyncPricesLoading(false);
            return;
        }

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
        };

        const shopId = String(shop.id || shop.url || '').trim();

        if (!shopId) {
            message.error('Shop ID is missing');
            setSyncPricesLoading(false);
            return;
        }

        if (!SYNC_PRICES_API_URL) {
            message.error('SYNC_PRICES_API_URL is not set');
            setSyncPricesLoading(false);
            return;
        }

        const url = new URL(SYNC_PRICES_API_URL);
        url.searchParams.set('shopId', shopId);
        const apiUrlWithQuery = url.toString();

        const payload: Record<string, any> = {
            force: true,
            promotionIds,
            trigger: 'manual'
        };

        axios.post(apiUrlWithQuery, payload, {
            headers: headers
        }).catch((error) => {
            if (error?.response?.status === 401 || error?.response?.status === 403) {
                message.error('Not authorized to sync this shop');
            }
            console.error('Error syncing prices (fire-and-forget):', error);
        });
        
        message.success('Price sync started successfully');
        setSyncPricesModalOpen(false);
        setSyncPricesLoading(false);
        setPromotionIds([]);
    };

    const handleSyncPricesCancel = () => {
        setSyncPricesModalOpen(false);
    };

    return (
        <>
            <Space direction="vertical" style={{ width: isMobile ? '100%' : 'auto' }} size="middle">
                <Button
                    type="primary"
                    icon={<SyncOutlined />}
                    size={isMobile ? 'large' : 'middle'}
                    onClick={handleSyncInventoryClick}
                    block={isMobile}
                    style={isMobile ? {} : { width: BUTTON_WIDTH }}
                >
                    Sync Inventory
                </Button>
                <Button
                    type="primary"
                    icon={<DollarOutlined />}
                    size={isMobile ? 'large' : 'middle'}
                    onClick={handleSyncPricesClick}
                    block={isMobile}
                    style={isMobile ? {} : { width: BUTTON_WIDTH }}
                >
                    Sync Prices
                </Button>
            </Space>
            <ConfirmModal
                open={syncInventoryModalOpen}
                title="Confirm Sync Inventory"
                message="This will sync the inventory quantities for the selected products."
                onConfirm={handleSyncInventoryConfirm}
                onCancel={handleSyncInventoryCancel}
                loading={syncInventoryLoading}
                confirmText={barcodes.length > 0 ? `Sync ${barcodes.length} products` : 'Sync all products'}
            >
                <Form.Item label="Barcodes" style={{ marginTop: '16px', marginBottom: 0 }}>
                    <CommaSeparatedInput
                        onChange={setBarcodes}
                        placeholder="Enter barcodes (comma-separated, optional)"
                    />
                </Form.Item>
            </ConfirmModal>
            <ConfirmModal
                open={syncPricesModalOpen}
                title="Confirm Sync Prices"
                message="This will sync the prices for all products."
                onConfirm={handleSyncPricesConfirm}
                onCancel={handleSyncPricesCancel}
                loading={syncPricesLoading}
                confirmText={promotionIds.length > 0 ? `Sync with ${promotionIds.length} promotions` : 'Sync without promotions'}
            >
                <Form.Item label="Promotion IDs" style={{ marginTop: '16px', marginBottom: 0 }}>
                    <CommaSeparatedInput
                        onChange={setPromotionIds}
                        placeholder="Enter promotion IDs (comma-separated, optional)"
                    />
                </Form.Item>
            </ConfirmModal>
        </>
    );
}

